<template>
  <!-- w-full so the field fills its container on its own. With inheritAttrs:false
       a parent's w-full now lands on the inner control, not this root, so the
       root must carry its own width or it collapses inside flex/grid parents. -->
  <div class="w-full" v-bind="rootAttrs">
    <FloatLabel variant="on" class="relative transition-colors duration-200 w-full block">
      <Textarea
        v-if="textarea"
        v-bind="controlAttrs"
        :modelValue="internalValue"
        :readonly="readonly"
        :disabled="disabled"
        :data-test-id="`${id}-textarea`"
        :id="id"
        :name="name"
        :aria-label="props.ariaLabel || (label ? $t(`accessibilityLabels.textarea-${label}`) : '')"
        :aria-invalid="invalid"
        :aria-describedby="
          invalid && errorMessage ? `${id}-errorMessage` : null
        "
        @update:modelValue="handleInput"
        class="bg-transparent !border-2 rounded px-[14px] !py-[16px]"
        :class="[inputClasses, width, height]"
        :rows="rows"
        :autoResize="autoResize"
        :maxlength="maxLength"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="emit('enter')"
      />
      <div v-else class="relative w-full">
        <span
          v-if="icon"
          class="absolute top-1/2 transform -translate-y-1/2 text-lg text-gray-500"
          :class="{
            'left-4': iconPosition === 'left',
            'right-4': iconPosition === 'right',
          }"
          v-html="icon"
        ></span>

        <InputText
          v-bind="controlAttrs"
          :key="showPassword ? 'text' : 'password'"
          :modelValue="internalValue"
          :readonly="readonly"
          :disabled="disabled"
          :id="id"
          :name="name"
          :aria-label="ariaLabel || (label ? $t(`accessibilityLabels.input-${label}`) : '')"
          :aria-invalid="invalid"
          :aria-describedby="
            invalid && errorMessage ? `${id}-errorMessage` : null
          "
          :data-test-id="`${id}-input`"
          @update:modelValue="handleInput"
          @keydown.enter="emit('enter')"
          class="bg-transparent !border-[2px] !rounded-[8px] !py-[16.5px] !text-labelColor !font-just !text-base"
          :class="[
            inputClasses,
            width,
            height,
            inputClass,
            icon
              ? iconPosition === 'left'
                ? '!pl-10'
                : '!pr-10'
              : '!px-[14px]',
            type === 'password' ? '!pr-14' : '',
          ]"
          :maxlength="maxLength"
          @focus="handleFocus"
          @blur="handleBlur"
          :type="showPassword ? 'text' : inputType"
          @keypress="handleKeypress"
        />

        <button
          type="button"
          v-if="type === 'password'"
          class="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer bg-transparent border-none p-0 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-deepSky rounded"
          @click="togglePasswordVisibility"
          :aria-label="showPassword ? $t('accessibilityLabels.hidePassword') : $t('accessibilityLabels.showPassword')"
        >
          <div
            v-html="
              showPassword
                ? eyeClose({ width: '24', height: '24', color: 'black' })
                : eyeOpen({ width: '24', height: '24', color: 'black' })
            "
          ></div>
        </button>
      </div>

      <label
        v-if="label"
        :for="id"
        class="absolute left-2 -top-2 !font-just !font-normal !text-[14px] sm:!text-[16px] pointer-events-none transition-all duration-200 flex gap-1"
        :class="[
          labelState ? labelClasses : null,
          labelClass,
          type === 'password' ? 'pr-14' : null,
        ]"
        :data-test-id="`${id}-label`"
      >
        <template v-if="required">
          {{ label }}<span v-if="focused || !internalValue">*</span>
        </template>
        <template v-else>
          <span
            :class="
              icon && iconPosition === 'left' && !focused && !internalValue
                ? 'ms-[20px] '
                : ''
            "
            >{{ label }}</span
          >
        </template>
      </label>
    </FloatLabel>

    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{
        '!text-error !first-letter:uppercase !first-letter:tracking-wider !px-[10px] !mt-[3.84px] !font-robo !font-400 !text-[15.36px]':
          invalid,
      }"
      :data-test-id="`${id}-errorMessage`"
      :id="`${id}-errorMessage`"
      role="alert"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script setup>
// Forward fallthrough attrs (tabindex, @focus/@click handlers, etc.) onto the
// inner InputText via v-bind="$attrs" below, NOT onto the wrapper <div>. Without
// this, a tabindex="0" passed by a parent (e.g. CustomField) lands on the root
// div AND the input, creating two tab stops per field. See CustomField.vue.
defineOptions({ inheritAttrs: false });

import { eyeOpen, eyeClose } from "@/utils/commonSvg";
import { ref, computed, watch } from "vue";
import { useControlAttrs } from "@/composables/useControlAttrs";

// class/style -> root (layout: grid col-span, width); the rest -> inner control
// (tabindex/listeners, the double-tab fix). See useControlAttrs.
const { rootAttrs, controlAttrs } = useControlAttrs();

const props = defineProps({
  inputClass: { type: String, default: '' },
  modelValue: [String, Number],
  label: String,
  labelClass: {},
  labelState: { type: Boolean, default: true },
  id: String,
  type: {
    type: String,
    default: "text",
  },
  textarea: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: "input",
  },
  required: Boolean,
  errorMessage: String,
  invalid: Boolean,
  maxLength: Number,
  minLength: Number,
  width: { type: String, default: "w-full" },
  height: { type: String, default: "h-auto" },
  rows: { type: Number, default: 3 },
  autoResize: { type: Boolean, default: true },
  readonly: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  autoTrim: { type: Boolean, default: true },
  icon: {
    type: String,
    default: null,
  },
  iconPosition: {
    type: String,
    default: "left",
    validator: (val) => ["left", "right"].includes(val),
  },
  digitOnly: {
    type: Boolean,
    default: false,
  },
  // Add new prop for decimal support
  allowDecimal: {
    type: Boolean,
    default: false,
  },
  // Add prop to limit decimal places
  decimalPlaces: {
    type: Number,
    default: 2,
  },
  pattern: {
    type: [String, RegExp],
    default: null,
  },
  phoneFormat: {
    type: String,
    default: null,
    validator: (val) => val === null || val === "us",
  },
  ariaLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "input", "blur", "enter"]);
const focused = ref(false);
const internalValue = ref(props.modelValue ?? "");
const showPassword = ref(false);

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

watch(
  () => props.modelValue,
  (val) => {
    if (val !== internalValue.value) {
      let formattedVal = val;
      internalValue.value = formattedVal;
    }
  }
);

const inputType = computed(() => {
  if (props.type === "number" && (props.digitOnly || props.allowDecimal)) {
    return "text";
  }
  return props.type;
});

const sanitizeDigits = (val) => String(val).replace(/\D/g, "");
const sanitizeDecimal = (val) => {
  if (val === null || val === undefined) return "";

  // Convert to string
  let str = String(val);

  // Remove all invalid characters except digits and dot
  str = str.replace(/[^0-9.]/g, "");

  // If more than one dot, keep only the first
  const parts = str.split(".");
  if (parts.length > 2) {
    str = parts[0] + "." + parts.slice(1).join("");
  }

  // Limit decimal places
  if (props.decimalPlaces >= 0 && str.includes(".")) {
    const [intPart, decPart] = str.split(".");
    str = intPart + "." + decPart.slice(0, props.decimalPlaces);
  }

  return str;
};
const handleInput = (value) => {
  let newVal = typeof value === "string" ? value.trimStart() : value;

  if (props.allowDecimal) newVal = sanitizeDecimal(newVal);
  else if (props.digitOnly) newVal = sanitizeDigits(newVal);

  if (props.pattern && typeof newVal === "string") {
    const regex =
      typeof props.pattern === "string"
        ? new RegExp(props.pattern)
        : props.pattern;
    if (!regex.test(newVal)) newVal = newVal.slice(0, -1);
  }

  if (props.maxLength && String(newVal).length > props.maxLength) {
    newVal = String(newVal).slice(0, props.maxLength);
  }

  internalValue.value = newVal;
  emit("update:modelValue", newVal);
  emit("input", newVal);
};

const handleFocus = () => {
  focused.value = true;
};

const handleBlur = (event) => {
  focused.value = false;

  if (props.autoTrim && typeof internalValue.value === "string") {
    const trimmed = internalValue.value.trim();
    if (trimmed !== internalValue.value) {
      internalValue.value = trimmed;
      emit("update:modelValue", trimmed);
      emit("input", trimmed);
    }
  }

  emit("blur", event);
};

const inputClasses = computed(() => ({
  "!border-deepSky": (focused.value || internalValue.value) && !props.invalid,
  "!border-softGray": !focused.value && !internalValue.value && !props.invalid,
  "!border-error": props.invalid,
}));

const labelClasses = computed(() => ({
  "!text-deepSky": (focused.value || internalValue.value) && !props.invalid,
  "!text-greyBlue": !focused.value && !internalValue.value && !props.invalid,
  "!text-error": props.invalid,
}));

const handleKeypress = (e) => {
  if (props.allowDecimal) {
    // Allow digits, decimal point, and navigation keys
    const allowedKeys = [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      ".",
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
    ];
    const isNavigationKey = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ].includes(e.key);

    if (
      !allowedKeys.includes(e.key) &&
      !isNavigationKey &&
      !e.ctrlKey &&
      !e.metaKey
    ) {
      e.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (e.key === "." && internalValue.value.includes(".")) {
      e.preventDefault();
      return;
    }
  } else if (props.digitOnly && !/\d/.test(e.key)) {
    // Original digit-only logic
    const isNavigationKey = [
      "Backspace",
      "Delete",
      "Tab",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ].includes(e.key);
    if (!isNavigationKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  }
};
</script>

<style scoped>
input[type="password"],
input[type="text"] {
  -webkit-appearance: none;
  appearance: none;
  line-height: normal;
}
input[type="password"]::-webkit-contacts-auto-fill-button,
input[type="password"]::-webkit-credentials-auto-fill-button {
  visibility: hidden;
  display: none !important;
  pointer-events: none;
  height: 0;
  width: 0;
  margin: 0;
}

input[type="email"]::-webkit-contacts-auto-fill-button,
input[type="email"]::-webkit-credentials-auto-fill-button {
  visibility: hidden;
  display: none !important;
  pointer-events: none;
  height: 0;
  width: 0;
  margin: 0;
}
</style>
